import React, { useState } from 'react';
import { FiTrash2, FiAlertTriangle, FiX, FiCheck, FiRefreshCw } from 'react-icons/fi';
import { clearAllProducts } from '../../api/productApi';

const Settings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isClearModalOpen, setIsClearModalOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');

  // Show success message with auto-dismiss after 3 seconds
  const showSuccess = (message: string) => {
    setSuccessMessage(message);
    setTimeout(() => {
      setSuccessMessage(null);
    }, 3000);
  };

  // Open the clear inventory modal
  const openClearModal = () => {
    setIsClearModalOpen(true);
    setConfirmText('');
  };

  // Close the clear inventory modal
  const closeClearModal = () => {
    setIsClearModalOpen(false);
    setConfirmText('');
  };

  // Handle clearing all products
  const handleClearAllProducts = async () => {
    if (confirmText.toLowerCase() !== 'delete all') {
      setError('Please type "delete all" to confirm');
      return;
    }
    
    try {
      setLoading(true);
      setError(null);
      
      await clearAllProducts();
      
      setIsClearModalOpen(false);
      showSuccess('All products have been cleared successfully!');
    } catch (err) {
      console.error('Error clearing products:', err);
      setError('Failed to clear products. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-background p-6 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold text-primary mb-6">Settings</h2>
      
      {error && (
        <div className="bg-red-500/10 border border-red-500/50 text-red-400 px-4 py-3 rounded-md mb-6">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="bg-green-500/10 border border-green-500/50 text-green-400 px-4 py-3 rounded-md mb-6 animate-fade-in">
          {successMessage}
        </div>
      )}
      
      <div className="space-y-6">
        {/* Inventory Management Section */}
        <div className="border border-gray-800 rounded-lg p-4">
          <h3 className="text-lg font-medium text-primary mb-4">Inventory Management</h3>
          
          <div className="space-y-4">
            <div className="bg-red-500/5 border border-red-500/20 rounded-md p-4">
              <div className="flex items-start">
                <FiAlertTriangle className="text-red-500 h-5 w-5 mr-3 mt-0.5" />
                <div>
                  <h4 className="text-red-400 font-medium mb-2">Danger Zone</h4>
                  <p className="text-gray-400 text-sm mb-4">
                    These actions are destructive and cannot be undone. Please proceed with caution.
                  </p>
                  
                  <button
                    onClick={openClearModal}
                    className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md flex items-center"
                  >
                    <FiTrash2 className="mr-2 h-4 w-4" />
                    Clear All Products
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Clear All Products Confirmation Modal */}
      {isClearModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 animate-fade-in">
          <div className="bg-background border border-gray-800 rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-semibold text-primary">Clear All Products</h3>
              <button onClick={closeClearModal} className="text-gray-400 hover:text-gray-300">
                <FiX className="h-5 w-5" />
              </button>
            </div>
            
            <div className="border-t border-b border-gray-800 py-4 mb-4">
              <div className="flex items-center text-red-500 mb-4">
                <FiAlertTriangle className="h-6 w-6 mr-2" />
                <span className="font-medium">Warning: This action cannot be undone</span>
              </div>
              
              <p className="text-secondary mb-4">
                This will permanently delete ALL products from your inventory. Images, product details, and all associated data will be lost forever.
              </p>
              
              <div className="bg-red-500/10 border border-red-500/30 p-3 rounded-md text-sm text-red-400">
                To confirm, please type <strong>delete all</strong> in the field below.
              </div>
              
              <div className="mt-4">
                <label htmlFor="confirmText" className="block text-sm font-medium text-gray-400 mb-1">
                  Confirmation
                </label>
                <input
                  id="confirmText"
                  type="text"
                  value={confirmText}
                  onChange={(e) => setConfirmText(e.target.value)}
                  placeholder="Type 'delete all' to confirm"
                  className="w-full px-3 py-2 bg-background-light border border-gray-700 rounded-md text-primary focus:outline-none focus:ring-1 focus:ring-red-500 focus:border-red-500"
                />
              </div>
            </div>
            
            <div className="flex justify-between">
              <button
                onClick={closeClearModal}
                className="px-4 py-2 bg-background-light hover:bg-gray-800 rounded-md text-primary flex items-center"
              >
                <FiX className="mr-2 h-4 w-4" />
                Cancel
              </button>
              
              <button
                onClick={handleClearAllProducts}
                disabled={confirmText.toLowerCase() !== 'delete all' || loading}
                className={`px-4 py-2 bg-red-600 rounded-md text-white flex items-center ${
                  confirmText.toLowerCase() === 'delete all' && !loading
                    ? 'hover:bg-red-700'
                    : 'opacity-50 cursor-not-allowed'
                }`}
              >
                {loading ? (
                  <>
                    <FiRefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <FiTrash2 className="mr-2 h-4 w-4" />
                    Clear All Products
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings; 